import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BottomNav } from '@/components/BottomNav';
import { getMyOrders, type ConsumerOrder, type OrderStatus } from '@/lib/api';
import { formatPrice } from '@/lib/format';
import { useLocale, useTk } from '@/lib/i18n';
import { useAuth } from '@/lib/use-auth';

type OrderTab = 'all' | 'pending' | 'usable' | 'refund';

const ORDER_TABS: Array<{ key: OrderTab; labelKey: string }> = [
  { key: 'all', labelKey: 'order.list.tab_all' },
  { key: 'pending', labelKey: 'order.list.tab_pending' },
  { key: 'usable', labelKey: 'order.list.tab_usable' },
  { key: 'refund', labelKey: 'order.list.tab_refund' },
];

/** Map tab to API status filter (undefined = no filter). */
function tabToStatusFilter(tab: OrderTab): OrderStatus | undefined {
  switch (tab) {
    case 'pending':
      return 'PENDING';
    case 'usable':
      return 'SUCCEEDED';
    case 'refund':
      return 'REFUNDED';
    default:
      return undefined;
  }
}

function orderStatusKey(status: string): string {
  return `order.status.${status.toLowerCase()}`;
}

function formatDateTime(value: string | null | undefined, locale: string): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

const PAGE_SIZE = 20;

export function MyOrdersPage(): React.ReactElement {
  const tk = useTk();
  const locale = useLocale();
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState<OrderTab>('all');
  const [state, setState] = useState<{
    status: 'loading' | 'success' | 'error' | 'unauth';
    items: ConsumerOrder[];
    errorMessage: string | null;
    page: number;
    total: number;
    loadingMore: boolean;
  }>({
    status: 'loading',
    items: [],
    errorMessage: null,
    page: 1,
    total: 0,
    loadingMore: false,
  });

  /** Reset and fetch page 1 when tab or auth changes. */
  const fetchPage1 = useCallback(
    (tab: OrderTab, token: string, signal: AbortSignal) => {
      setState((prev) => ({
        ...prev,
        status: 'loading',
        items: [],
        page: 1,
        total: 0,
        loadingMore: false,
      }));
      getMyOrders(
        { page: 1, pageSize: PAGE_SIZE, status: tabToStatusFilter(tab) },
        token,
        signal,
      )
        .then((res) =>
          setState((prev) => ({
            ...prev,
            status: 'success',
            items: res.items,
            page: res.page,
            total: res.total,
            loadingMore: false,
          })),
        )
        .catch((err: Error) => {
          if (signal.aborted) return;
          setState((prev) => ({
            ...prev,
            status: 'error',
            errorMessage: err.message,
            loadingMore: false,
          }));
        });
    },
    [],
  );

  /** Load next page (append). */
  const loadMore = useCallback(() => {
    if (!auth.token) return;
    setState((prev) => ({ ...prev, loadingMore: true }));
    const ac = new AbortController();
    const nextPage = state.page + 1;
    getMyOrders(
      { page: nextPage, pageSize: PAGE_SIZE, status: tabToStatusFilter(activeTab) },
      auth.token,
      ac.signal,
    )
      .then((res) =>
        setState((prev) => ({
          ...prev,
          status: 'success',
          items: [...prev.items, ...res.items],
          page: res.page,
          total: res.total,
          loadingMore: false,
        })),
      )
      .catch((err: Error) => {
        if (ac.signal.aborted) return;
        setState((prev) => ({ ...prev, loadingMore: false, errorMessage: err.message }));
      });
  }, [auth.token, activeTab, state.page]);

  // Fetch page 1 on mount / auth change / tab change
  useEffect(() => {
    if (auth.status === 'idle' || auth.status === 'authenticating') {
      setState((prev) => ({ ...prev, status: 'loading', items: [], page: 1, total: 0 }));
      return;
    }

    if (auth.status !== 'authenticated' || !auth.token) {
      setState((prev) => ({ ...prev, status: 'unauth', items: [], page: 1, total: 0 }));
      return;
    }

    const ac = new AbortController();
    fetchPage1(activeTab, auth.token, ac.signal);
    return () => ac.abort();
  }, [auth.status, auth.token, activeTab, fetchPage1]);

  const hasMore = state.items.length < state.total;

  /** Copy voucher code to clipboard. */
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyVoucher = useCallback(
    (code: string, orderId: string) => {
      navigator.clipboard.writeText(code).catch(() => {
        // Fallback: select text range
        try {
          const el = document.createElement('textarea');
          el.value = code;
          document.body.appendChild(el);
          el.select();
          document.execCommand('copy');
          document.body.removeChild(el);
        } catch {
          // Clipboard unavailable — silently fail
        }
      });
      setCopiedId(orderId);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopiedId(null), 2000);
    },
    [],
  );

  return (
    <main className="min-h-screen px-6 py-8 space-y-4 pb-24">
      <header className="flex items-center justify-between">
        <Link to="/" className="text-[var(--tg-link)] text-sm">
          ← {tk('nav.home')}
        </Link>
        <h1 className="text-xl font-semibold">{tk('order.list.title')}</h1>
        <span />
      </header>

      {state.status === 'unauth' ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center text-neutral-500">
          {tk('order.list.unauth')}
        </div>
      ) : null}

      {state.status === 'loading' ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center text-neutral-500">
          {tk('common.loading')}
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div className="rounded-lg border border-danger-500 bg-danger-50 p-4 text-sm text-danger-700">
          <p className="font-medium">{tk('common.error.generic')}</p>
          <p className="text-xs opacity-75 mt-1 break-all">{state.errorMessage}</p>
        </div>
      ) : null}

      {state.status === 'success' ? (
        <>
          <div className="grid grid-cols-4 gap-2 rounded-2xl bg-neutral-100 p-1">
            {ORDER_TABS.map((tab) => {
              const selected = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setActiveTab(tab.key)}
                  className={`rounded-xl px-2 py-2 text-xs font-medium transition-colors ${
                    selected
                      ? 'bg-white text-brand-600 shadow-sm'
                      : 'text-neutral-500 hover:text-neutral-800'
                  }`}
                >
                  {tk(tab.labelKey)}
                </button>
              );
            })}
          </div>

          {state.items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center text-neutral-500">
              {tk('order.list.empty')}
            </div>
          ) : (
            <>
              <ul className="space-y-3">
                {state.items.map((order) => (
                  <li key={order.id}>
                    <Link
                      to={`/orders/${order.id}`}
                      className="block rounded-2xl border border-neutral-200 bg-white p-4 hover:border-brand-500"
                    >
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-xs text-[var(--tg-hint)]">
                            {order.orderNumber}
                          </p>
                          <p className="text-sm mt-1">
                            {tk('order.detail.quantity')} × {order.quantity ?? 1}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700">
                            {tk(orderStatusKey(order.status), { fallback: order.status })}
                          </span>
                          <span className="text-brand-500 font-semibold">
                            {formatPrice(order.totalAmountCents, order.currency)}
                          </span>
                        </div>
                      </div>

                      {order.status === 'SUCCEEDED' || order.status === 'VOUCHERED' ? (
                        <div className="mt-3 rounded-xl border border-dashed border-brand-200 bg-brand-50/60 p-3 text-sm">
                          {order.voucher ? (
                            <>
                              <div className="flex items-center justify-between gap-3">
                                <span className="text-[var(--tg-hint)]">
                                  {tk('order.list.voucher_code')}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copyVoucher(order.voucher!.code, order.id);
                                  }}
                                  className="font-mono text-base font-semibold tracking-widest text-brand-700 hover:text-brand-500 transition-colors cursor-pointer"
                                  title={tk('common.copy')}
                                >
                                  {copiedId === order.id
                                    ? tk('common.copied')
                                    : order.voucher.code}
                                </button>
                              </div>
                              <div className="mt-1 flex items-center justify-between gap-3 text-xs text-[var(--tg-hint)]">
                                <span>{tk('order.list.valid_until')}</span>
                                <span>{formatDateTime(order.voucher.validUntil, locale)}</span>
                              </div>
                            </>
                          ) : (
                            <span className="text-[var(--tg-hint)]">
                              {tk('order.list.voucher_pending')}
                            </span>
                          )}
                        </div>
                      ) : null}

                      <p className="mt-3 text-right text-xs font-medium text-[var(--tg-link)]">
                        {tk('order.list.view_detail')}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>

              {hasMore ? (
                <div className="flex justify-center pt-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      loadMore();
                    }}
                    disabled={state.loadingMore}
                    className="rounded-xl border border-neutral-200 bg-white px-6 py-2 text-sm font-medium text-brand-600 hover:bg-brand-50 transition-colors disabled:opacity-50"
                  >
                    {state.loadingMore ? tk('common.loading') : tk('order.list.load_more')}
                  </button>
                </div>
              ) : state.items.length > 0 ? (
                <p className="text-center text-xs text-[var(--tg-hint)] pt-2">
                  {tk('order.list.all_loaded')}
                </p>
              ) : null}
            </>
          )}
        </>
      ) : null}

      <BottomNav />
    </main>
  );
}
